// AdminWidgetsSpec.integrated.js — 통합 메뉴 위젯 spec 동적 생성 (2026-06-10)
//
// 사용자 명세 (2026-06-10):
//   "5개 통합 메뉴 = 5개 위젯 (admin_integrated_4/13/14/15/16) 자동 등록"
//   "위젯 코드 단일 prefix: admin_integrated_{menuId}"
//   "feature_type 별 render 분기 (renewal/consultant/leave/attendance/calendar)"
//   "사이즈 S 통일"
//   "라벨 = data.menuName 정확히 표시 — 'fallback 통합 메뉴' 금지"
//
// 데이터 응답 형식 (admin-widgets-data.js 의 runIntegratedWidget):
//   { menuId, menuName, featureType, companies, lines: [{ companyCode, payload }] }

import {
  FileClock,
  Users,
  Calendar,
  Clock,
  CalendarDays,
} from 'lucide-react';
import { WIDTHS } from './widgetFormat';
import {
  renderUnknown,
  renderRenewalIntegrated,
  renderConsultantIntegrated,
  renderLeaveIntegrated,
  renderAttendanceIntegrated,
  renderCalendarIntegrated,
} from './AdminWidgetsSpec.integrated-renders';

// 통합 위젯 단일 prefix
const INTEGRATED_PREFIX = 'admin_integrated_';

// feature_type 별 메타 + render + size(옵션, 기본 'S').
//   attendance 는 출퇴근 통계와 동일 UI(출근/퇴근/미퇴근 + 출근중 이름)라 'L' (회사별 블록 수용).
const FEATURE_SPEC = {
  contract_renewal: {
    Icon: FileClock,
    accent: 'indigo',
    render: renderRenewalIntegrated,
  },
  marketer_consultant: {
    Icon: Users,
    accent: 'violet',
    render: renderConsultantIntegrated,
  },
  leave: {
    Icon: Calendar,
    accent: 'emerald',
    render: renderLeaveIntegrated,
  },
  attendance: {
    Icon: Clock,
    accent: 'sky',
    render: renderAttendanceIntegrated,
    size: 'L',
  },
  calendar: {
    Icon: CalendarDays,
    accent: 'amber',
    render: renderCalendarIntegrated,
  },
};

// 통합 위젯 코드를 prefix 매칭해 spec 객체 동적 생성.
// 라벨은 data.menuName 우선 — 사용자 명세상 fallback 'fallback 통합 메뉴' 금지.
//   data 가 없을 땐 menuId 만 표시 (data 도착 후 라벨 정확화).
// feature_type 도 data 에서 — data 없으면 기본 아이콘 + 회색 라인.
export function buildIntegratedSpec(code, data) {
  if (typeof code !== 'string') return null;
  if (!code.startsWith(INTEGRATED_PREFIX)) return null;
  const tail = code.slice(INTEGRATED_PREFIX.length);
  if (!/^\d+$/.test(tail)) return null;
  const menuId = Number(tail);

  // featureType 별 메타 — data 가 아직 없으면 기본 아이콘 사용
  const ft = data && typeof data.featureType === 'string' ? data.featureType : null;
  const featureSpec = (ft && FEATURE_SPEC[ft]) || null;
  const Icon = featureSpec ? featureSpec.Icon : CalendarDays;
  const accent = featureSpec ? featureSpec.accent : 'slate';
  const render = featureSpec ? featureSpec.render : renderUnknown;
  // feature_type 별 size override (없으면 'S' 통일)
  const size = (featureSpec && featureSpec.size) || 'S';

  // 라벨 — data.menuName 우선 (사용자 명세: 메뉴 이름 정확 표시)
  const menuName = data && typeof data.menuName === 'string' && data.menuName
    ? data.menuName
    : `통합 메뉴 #${menuId}`;

  return {
    Icon,
    label: `${menuName} (통합)`,
    width: WIDTHS.WIDE,
    accent,
    size, // 기본 'S', feature_type override (attendance='L')
    render,
    watermarkIcon: Icon,
  };
}

// 위젯 코드가 통합 위젯 prefix 에 매칭되는지 (정수 menuId tail 까지 확인)
export function isIntegratedWidgetCode(code) {
  if (typeof code !== 'string') return false;
  if (!code.startsWith(INTEGRATED_PREFIX)) return false;
  const tail = code.slice(INTEGRATED_PREFIX.length);
  return /^\d+$/.test(tail);
}
